import { 
  Chip, 
  FormControl, 
  InputLabel, 
  MenuItem, 
  Select, 
  TextField,
  Checkbox,
  FormControlLabel,
  Popover,
  Box,
  Button,
  styled,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  IconButton,
  Typography,
  InputAdornment
} from "@mui/material";
import { IActivity, ITemplate } from "./types";
import { CampaignTypesPanel } from "./campaigns/CampaignTypesPanel";
import { CampaignDraft, CampaignPanelState } from "./campaigns/types";
import { useState, useEffect } from "react";
import { StyledInputLabel, StyledSelect, StyledTextField } from "./styled";
import { defaultBeneficiaryFieldsPj, defaultBeneficiaryFieldsClt } from "./defaultBeneficiaryFields";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';

// Styled components for the Editable Fields selector
const StyledEditableFieldsContainer = styled(Box)({
  border: '1px solid rgba(0, 0, 0, 0.23)',
  borderRadius: '8px',
  padding: '16px',
  minHeight: '56px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  cursor: 'pointer',
  position: 'relative',
  '&:hover': {
    borderColor: 'rgb(121, 121, 121)',
  },
});

const StyledEditableFieldsLabel = styled('label')({
  position: 'absolute',
  top: '-8px',
  left: '12px',
  background: 'white',
  padding: '0 4px',
  fontSize: '14px',
  color: 'rgba(15, 15, 30, 0.6)',
});

const StyledChipsContainer = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px',
  flex: 1,
});

const StyledEditableChip = styled(Chip)({
  backgroundColor: '#3024e1',
  color: 'white',
  '& .MuiChip-deleteIcon': {
    color: 'rgba(255, 255, 255, 0.7)',
    '&:hover': {
      color: 'white',
    },
  },
});

const StyledDropdownContainer = styled(Box)({
  padding: '8px',
  maxHeight: '200px',
  overflowY: 'auto',
});

// Styled components for the Beneficiary Fields Config
const StyledBeneficiaryFieldsContainer = styled(Box)({
  border: '1px solid rgba(0, 0, 0, 0.23)',
  borderRadius: '8px',
  padding: '16px',
  minHeight: '56px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  position: 'relative',
  '&:hover': {
    borderColor: 'rgb(121, 121, 121)',
  },
});

const StyledBeneficiaryLabel = styled('label')({
  position: 'absolute',
  top: '-8px',
  left: '12px',
  background: 'white',
  padding: '0 4px',
  fontSize: '14px',
  color: 'rgba(15, 15, 30, 0.6)',
});

const StyledBeneficiaryChipsContainer = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px',
  flex: 1,
});

const StyledBeneficiaryChip = styled(Chip)({
  backgroundColor: '#3024e1',
  color: 'white',
});

const StyledSearchField = styled(TextField)({
  marginBottom: '8px',
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderRadius: '8px',
    },
  },
});

interface IProps {
  index: number;
  activity: IActivity;
  handleChangeActivity: (activityIndex: number, payload: IActivity) => void;
  allowedLayouts: {title: string, id: string}[];
  /** Spec campanha de contributários §4: no template Conferir Movimentações, o app Campanhas é o painel por tipo. */
  campaignPanel?: {
    state: CampaignPanelState | null;
    draft: CampaignDraft;
    onChange: (draft: CampaignDraft) => void;
    error: string | null;
    onRetry: () => void;
  };
}

// Mock data for editable fields
interface EditableField {
  id: string;
  name: string;
}

const mockEditableFields: EditableField[] = [
  { id: "name", name: "Nome completo" },
  // { id: "taxPayerId", name: "CPF beneficiário" },
  // { id: "taxPayerIdEmployee", name: "CPF titular" },
  { id: "birthDate", name: "Data de nascimento" },
  { id: "motherName", name: "Nome da mãe" },
  { id: "weddingDate", name: "Data de casamento" },
  { id: "type", name: "Tipo" },
  { id: "organizationId", name: "CNPJ da organização" },
  { id: "cnpj", name: "CNPJ empresa" },
  { id: "pis", name: "PIS" },
  { id: "genderId", name: "Gênero" },
  { id: "raceId", name: "Raça" },
  { id: "maritalStatusId", name: "Estado Civil" },
  { id: "educationLevelId", name: "Escolaridade" },
  { id: "socialName", name: "Nome Social" },
  { id: "birthCountryId", name: "País Nascimento" },
  { id: "nationalityCountryId", name: "Nacionalidade" },
  { id: "birthStateId", name: "Estado Nascimento" },
  { id: "birthCityId", name: "Cidade Nascimento" },
  { id: "fatherName", name: "Nome do Pai" },
  { id: "zipCode", name: "CEP" },
  { id: "addressType", name: "Tipo Endereço" },
  { id: "address", name: "Endereço" },
  { id: "addressNumber", name: "Número" },
  { id: "complement", name: "Complemento" },
  { id: "neighborhood", name: "Bairro" },
  { id: "stateId", name: "Estado" },
  { id: "cityId", name: "Cidade" },
  { id: "nationalIdentityNumber", name: "RG" },
  { id: "nationalIdentityEmissor", name: "Órgão Emissor RG" },
  { id: "nationalIdentityExpeditionDate", name: "Data Expedição RG" },
  { id: "phoneNumber", name: "Telefone" },
  { id: "email", name: "E-mail" },
  { id: "hiringDate", name: "Data de admissão" },
  
  // New fields from layoutFields.txt
  { id: "status", name: "Status" },
  { id: "jobTitle", name: "Cargo" },
  { id: "paymentFrequency", name: "Periodicidade Pagamento" },
  { id: "grossPayment", name: "Pagamento Bruto" },
  { id: "municipality", name: "Município" },
  { id: "cbo", name: "CBO" },
  { id: "dismissalDate", name: "Data Demissão" },
  // { id: "transferred", name: "Transferido" },
  // { id: "plan", name: "Plano" },
  
  { id: "employeeId", name: "ID Funcionário" },
  { id: "dependentTypeId", name: "Tipo Dependente" },
  // { id: "isDeleted", name: "Excluído" },
  { id: "isStudent", name: "Estudante" },
  // { id: "createdAt", name: "Criado em" },
  // { id: "updatedAt", name: "Atualizado em" },
];
// scheduling: "",
// isAutomatic: "",
// layoutId: "",
export function ActivitySetupConfig({
  activity,
  index,
  handleChangeActivity,
  allowedLayouts,
  campaignPanel,
}: IProps) {
  const [layout, setLayout] = useState<string | null>(null);
  
  // State for editable fields
  const getSelectedFieldsFromActivity = (): EditableField[] => {
    const editableFieldIds = activity.params.editableFields || [];
    return mockEditableFields.filter(field => editableFieldIds.includes(field.id));
  };

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  
  // State for beneficiary fields PJ
  const [visibleFieldsPj, setVisibleFieldsPj] = useState<{ name: string, visible: boolean }[]>([]);
  const [isBeneficiaryModalOpenPj, setIsBeneficiaryModalOpenPj] = useState<boolean>(false);
  
  // State for beneficiary fields CLT
  const [visibleFieldsClt, setVisibleFieldsClt] = useState<{ name: string, visible: boolean }[]>([]);
  const [isBeneficiaryModalOpenClt, setIsBeneficiaryModalOpenClt] = useState<boolean>(false);
  
  // Get selected fields from activity params
  const selectedFields = getSelectedFieldsFromActivity();

  function handleEdit(arg0: any): void {
    throw new Error("Function not implemented.");
  }

  // Helper functions for editable fields
  const handleOpenDropdown = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseDropdown = () => {
    setAnchorEl(null);
    setSearchTerm('');
  };

  const handleFieldToggle = (field: EditableField) => {
    const currentEditableFields = activity.params.editableFields || [];
    const isSelected = currentEditableFields.includes(field.id);
    
    let newEditableFields: string[];
    if (isSelected) {
      newEditableFields = currentEditableFields.filter(id => id !== field.id);
    } else {
      newEditableFields = [...currentEditableFields, field.id];
    }
    
    handleChangeActivity(index, {
      ...activity,
      params: {
        ...activity.params,
        editableFields: newEditableFields
      }
    });
  };

  const handleRemoveField = (fieldId: string) => {
    const currentEditableFields = activity.params.editableFields || [];
    const newEditableFields = currentEditableFields.filter(id => id !== fieldId);
    
    handleChangeActivity(index, {
      ...activity,
      params: {
        ...activity.params,
        editableFields: newEditableFields
      }
    });
  };

  const filteredFields = mockEditableFields.filter(field =>
    field.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const isDropdownOpen = Boolean(anchorEl);

  // Helper functions for beneficiary fields

  // Lista de campos que são sempre obrigatórios e não podem ser editados
  const mandatoryFieldsPj = ["taxPayerId", "name", "legalEntityId"];
  const mandatoryFieldsClt = ["taxPayerId", "name"]; // CLT não tem legalEntityId

  // Função para obter estado inicial PJ baseado nos parâmetros da atividade
  const getInitialBeneficiaryFieldsPj = () => {
    // Se existe beneficiaryFields nos params da atividade (modo edição)
    if (activity.params.beneficiaryFields && activity.params.beneficiaryFields.length > 0) {
      console.log("🔄 MODO EDIÇÃO: Fazendo merge dos dados salvos com lista completa");
      
      // Criar um mapa dos campos salvos para busca rápida
      const savedFieldsMap = new Map();
      activity.params.beneficiaryFields.forEach(savedField => {
        savedFieldsMap.set(savedField.key, savedField);
      });
      
      // Fazer merge: todos os campos padrão + dados salvos
      const mergedFields = defaultBeneficiaryFieldsPj.map(defaultField => {
        const savedField = savedFieldsMap.get(defaultField.key);
        
        // Se é um campo obrigatório, sempre forçar visible=true, mandatory=true
        if (mandatoryFieldsPj.includes(defaultField.key)) {
          return {
            key: defaultField.key,
            label: defaultField.label,
            visible: true,  // Sempre visível
            mandatory: true // Sempre obrigatório
          };
        }
        
        if (savedField) {
          // Se o campo foi salvo, usar os dados salvos
          return {
            key: defaultField.key,
            label: defaultField.label, // Manter label padrão
            visible: savedField.visible,
            mandatory: savedField.mandatory
          };
        } else {
          // Se o campo não foi salvo, usar valores padrão (false, false)
          return defaultField;
        }
      });
      
      console.log("📋 Campos após merge:", mergedFields);
      console.log("📊 Total de campos no modal:", mergedFields.length);
      console.log("📊 Campos configurados:", mergedFields.filter(f => f.visible || f.mandatory).length);
      
      return mergedFields;
    }
    
    // Caso contrário, usar estado padrão (modo criação)
    console.log("✨ MODO CRIAÇÃO: Carregando estado padrão", defaultBeneficiaryFieldsPj);
    return defaultBeneficiaryFieldsPj;
  };

  // Função para obter estado inicial CLT baseado nos parâmetros da atividade
  const getInitialBeneficiaryFieldsClt = () => {
    // Se existe cltFields nos params da atividade (modo edição)
    if (activity.params.cltFields && activity.params.cltFields.length > 0) {
      console.log("🔄 MODO EDIÇÃO CLT: Fazendo merge dos dados salvos com lista completa");
      
      // Criar um mapa dos campos salvos para busca rápida
      const savedFieldsMap = new Map();
      activity.params.cltFields.forEach(savedField => {
        savedFieldsMap.set(savedField.key, savedField);
      });
      
      // Fazer merge: todos os campos padrão + dados salvos
      const mergedFields = defaultBeneficiaryFieldsClt.map(defaultField => {
        const savedField = savedFieldsMap.get(defaultField.key);
        
        // Se é um campo obrigatório, sempre forçar visible=true, mandatory=true
        if (mandatoryFieldsClt.includes(defaultField.key)) {
          return {
            key: defaultField.key,
            label: defaultField.label,
            visible: true,  // Sempre visível
            mandatory: true // Sempre obrigatório
          };
        }
        
        if (savedField) {
          // Se o campo foi salvo, usar os dados salvos
          return {
            key: defaultField.key,
            label: defaultField.label, // Manter label padrão
            visible: savedField.visible,
            mandatory: savedField.mandatory
          };
        } else {
          // Se o campo não foi salvo, usar valores padrão (false, false)
          return defaultField;
        }
      });
      
      console.log("📋 Campos CLT após merge:", mergedFields);
      console.log("📊 Total de campos CLT no modal:", mergedFields.length);
      console.log("📊 Campos CLT configurados:", mergedFields.filter(f => f.visible || f.mandatory).length);
      
      return mergedFields;
    }
    
    // Caso contrário, usar estado padrão (modo criação)
    console.log("✨ MODO CRIAÇÃO CLT: Carregando estado padrão", defaultBeneficiaryFieldsClt);
    return defaultBeneficiaryFieldsClt;
  };

  const [fieldsToEditDefaultPj, setFieldsToEditDefaultPj] = useState(getInitialBeneficiaryFieldsPj());
  const [tempFieldsStatePj, setTempFieldsStatePj] = useState(getInitialBeneficiaryFieldsPj());
  const [searchModalTermPj, setSearchModalTermPj] = useState<string>('');
  
  const [fieldsToEditDefaultClt, setFieldsToEditDefaultClt] = useState(getInitialBeneficiaryFieldsClt());
  const [tempFieldsStateClt, setTempFieldsStateClt] = useState(getInitialBeneficiaryFieldsClt());
  const [searchModalTermClt, setSearchModalTermClt] = useState<string>('');

  // Funções para o modal
  const handleOpenBeneficiaryModalPj = () => {
    setTempFieldsStatePj([...fieldsToEditDefaultPj]);
    setIsBeneficiaryModalOpenPj(true);
  };

  const handleOpenBeneficiaryModalClt = () => {
    setTempFieldsStateClt([...fieldsToEditDefaultClt]);
    setIsBeneficiaryModalOpenClt(true);
  };

  const handleCloseBeneficiaryModalPj = () => {
    setIsBeneficiaryModalOpenPj(false);
    setSearchModalTermPj('');
  };

  const handleCancelModalPj = () => {
    setTempFieldsStatePj([...fieldsToEditDefaultPj]);
    handleCloseBeneficiaryModalPj();
  };

  const handleCloseBeneficiaryModalClt = () => {
    setIsBeneficiaryModalOpenClt(false);
    setSearchModalTermClt('');
  };

  const handleCancelModalClt = () => {
    setTempFieldsStateClt([...fieldsToEditDefaultClt]);
    handleCloseBeneficiaryModalClt();
  };

  const handleSaveModalPj = () => {
    // Filtrar apenas campos que foram configurados (visíveis ou obrigatórios)
    const configuredFields = tempFieldsStatePj.filter(field => field.visible || field.mandatory);
    
    console.log("💾 Salvando configuração de Campos Pessoa Jurídica:");
    console.log("📊 Total de campos disponíveis:", tempFieldsStatePj.length);
    console.log("📊 Campos configurados para salvar:", configuredFields.length);
    console.log("📋 Campos que serão salvos:", configuredFields);
    
    // Atualizar estado local
    setFieldsToEditDefaultPj([...tempFieldsStatePj]);
    
    // Salvar nos parâmetros da atividade APENAS os campos configurados
    handleChangeActivity(index, {
      ...activity,
      params: {
        ...activity.params,
        beneficiaryFields: configuredFields  // ⭐ CORREÇÃO: Salvar apenas campos configurados
      }
    });

    console.log("📤 Dados salvos nos parâmetros da atividade para envio ao backend");

    // Atualizar chips visíveis
    const newVisibleFieldsPj = tempFieldsStatePj
      .filter(field => field.visible)
      .map(field => ({ name: field.label, visible: true }));
    setVisibleFieldsPj(newVisibleFieldsPj);
    
    handleCloseBeneficiaryModalPj();
  };

  const handleSaveModalClt = () => {
    // Filtrar apenas campos que foram configurados (visíveis ou obrigatórios)
    const configuredFields = tempFieldsStateClt.filter(field => field.visible || field.mandatory);
    
    console.log("💾 Salvando configuração de Campos CLT:");
    console.log("📊 Total de campos disponíveis:", tempFieldsStateClt.length);
    console.log("📊 Campos configurados para salvar:", configuredFields.length);
    console.log("📋 Campos que serão salvos:", configuredFields);
    
    // Atualizar estado local
    setFieldsToEditDefaultClt([...tempFieldsStateClt]);
    
    // Salvar nos parâmetros da atividade APENAS os campos configurados
    handleChangeActivity(index, {
      ...activity,
      params: {
        ...activity.params,
        cltFields: configuredFields  // ⭐ Salvar campos CLT
      }
    });

    console.log("📤 Dados CLT salvos nos parâmetros da atividade para envio ao backend");

    // Atualizar chips visíveis
    const newVisibleFieldsClt = tempFieldsStateClt
      .filter(field => field.visible)
      .map(field => ({ name: field.label, visible: true }));
    setVisibleFieldsClt(newVisibleFieldsClt);
    
    handleCloseBeneficiaryModalClt();
  };

  const handleFieldToggleModalPj = (key: string, field: 'visible' | 'mandatory') => {
    // Não permitir edição dos campos obrigatórios
    if (mandatoryFieldsPj.includes(key)) {
      console.log(`⚠️ Campo PJ "${key}" é obrigatório e não pode ser editado`);
      return;
    }

    setTempFieldsStatePj(prevFields => 
      prevFields.map(f => {
        if (f.key === key) {
          if (field === 'visible') {
            // Se está tornando o campo invisível, também torna não obrigatório
            return { ...f, visible: !f.visible, mandatory: !f.visible ? false : f.mandatory };
          } else {
            // Toggle normal para mandatory
            return { ...f, [field]: !f[field] };
          }
        }
        return f;
      })
    );
  };

  const handleFieldToggleModalClt = (key: string, field: 'visible' | 'mandatory') => {
    // Não permitir edição dos campos obrigatórios
    if (mandatoryFieldsClt.includes(key)) {
      console.log(`⚠️ Campo CLT "${key}" é obrigatório e não pode ser editado`);
      return;
    }

    setTempFieldsStateClt(prevFields => 
      prevFields.map(f => {
        if (f.key === key) {
          if (field === 'visible') {
            // Se está tornando o campo invisível, também torna não obrigatório
            return { ...f, visible: !f.visible, mandatory: !f.visible ? false : f.mandatory };
          } else {
            // Toggle normal para mandatory
            return { ...f, [field]: !f[field] };
          }
        }
        return f;
      })
    );
  };

  // Filtrar campos PJ baseado na busca
  const filteredModalFieldsPj = tempFieldsStatePj.filter(field =>
    field.label.toLowerCase().includes(searchModalTermPj.toLowerCase())
  );
  
  // Filtrar campos CLT baseado na busca
  const filteredModalFieldsClt = tempFieldsStateClt.filter(field =>
    field.label.toLowerCase().includes(searchModalTermClt.toLowerCase())
  );

  // Sincronizar com dados da atividade quando componente for carregado
  useEffect(() => {
    // Sincronizar PJ
    const initialFieldsPj = getInitialBeneficiaryFieldsPj();
    setFieldsToEditDefaultPj(initialFieldsPj);
    setTempFieldsStatePj(initialFieldsPj);
    
    const newVisibleFieldsPj = initialFieldsPj
      .filter(field => field.visible)
      .map(field => ({ name: field.label, visible: true }));
    setVisibleFieldsPj(newVisibleFieldsPj);
    
    // Sincronizar CLT
    const initialFieldsClt = getInitialBeneficiaryFieldsClt();
    setFieldsToEditDefaultClt(initialFieldsClt);
    setTempFieldsStateClt(initialFieldsClt);
    
    const newVisibleFieldsClt = initialFieldsClt
      .filter(field => field.visible)
      .map(field => ({ name: field.label, visible: true }));
    setVisibleFieldsClt(newVisibleFieldsClt);
  }, [activity.params.beneficiaryFields, activity.params.cltFields]); // Re-executar quando fields mudarem

  return (
    <>
      {/* Modal PJ */}
      <Modal open={isBeneficiaryModalOpenPj} onClose={handleCloseBeneficiaryModalPj}>
        <Box sx={{ 
          position: "absolute" as "absolute", 
          top: "50%", 
          left: "50%", 
          transform: "translate(-50%, -50%)", 
          height: "600px", 
          width: "100%", 
          maxWidth: 900, 
          bgcolor: "background.paper", 
          boxShadow: 24, 
          borderRadius: "1rem", 
          border: "none", 
          outline: "none", 
          padding: "24px", 
          display: "flex", 
          flexDirection: "column", 
          gap: "16px",
          overflow: "hidden"
        }}>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6" sx={{ fontSize: '20px', fontWeight: 600, color: '#000' }}>
              Campos Pessoa Jurídica (PJ)
            </Typography>
            <IconButton onClick={handleCloseBeneficiaryModalPj} sx={{ color: '#666' }}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Subtitle */}
          <Typography variant="body2" sx={{ color: 'rgba(0, 0, 0, 0.6)', mb: 2 }}>
            Configure os campos que serão necessários para campanhas de dados faltantes
          </Typography>

          {/* Search Field */}
          <TextField
            fullWidth
            placeholder="Digite para pesquisar"
            value={searchModalTermPj}
            onChange={(e) => setSearchModalTermPj(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#999' }} />
                </InputAdornment>
              ),
            }}
            sx={{ 
              mb: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
              }
            }}
          />

          {/* Table Container */}
          <Box sx={{ flexGrow: 1, maxHeight: '400px', overflow: 'auto' }}>
            <TableContainer sx={{ "& .MuiTableCell-root": { background: "#FFFFFF !important" } }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, color: '#000', fontSize: '14px', padding: '12px 16px', backgroundColor: '#fff !important' }}>
                      Campo
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#000', fontSize: '14px', padding: '12px 16px', textAlign: 'center', backgroundColor: '#fff !important' }}>
                      Visível
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#000', fontSize: '14px', padding: '12px 16px', textAlign: 'center', backgroundColor: '#fff !important' }}>
                      Obrigatoriedade
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredModalFieldsPj.map((field) => (
                    <TableRow key={field.key} sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}>
                      <TableCell sx={{ padding: '12px 16px', fontSize: '14px', color: '#000' }}>
                        {field.label}
                      </TableCell>
                      <TableCell sx={{ padding: '12px 16px', textAlign: 'center' }}>
                        <Switch
                          checked={field.visible}
                          disabled={mandatoryFieldsPj.includes(field.key)}
                          onChange={() => handleFieldToggleModalPj(field.key, 'visible')}
                          color="primary"
                          size="small"
                        />
                      </TableCell>
                      <TableCell sx={{ padding: '12px 16px', textAlign: 'center' }}>
                        <Switch
                          checked={field.mandatory}
                          disabled={!field.visible || mandatoryFieldsPj.includes(field.key)}
                          onChange={() => handleFieldToggleModalPj(field.key, 'mandatory')}
                          color="primary"
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
            <Button
              variant="outlined"
              color="error"
              style={{
                paddingTop: "0.25rem",
                paddingBottom: "0.25rem",
                paddingLeft: "1rem",
                paddingRight: "1rem",
                borderRadius: "0.375rem",
                height: "2.5rem",
                fontSize: "0.875rem",
                lineHeight: "1.25rem",
                fontWeight: 600,
                width: "200px",
              }}
              onClick={handleCancelModalPj}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              style={{
                paddingTop: "0.25rem",
                paddingBottom: "0.25rem",
                paddingLeft: "1rem",
                paddingRight: "1rem",
                borderRadius: "0.375rem",
                height: "2.5rem",
                fontSize: "0.875rem",
                lineHeight: "1.25rem",
                fontWeight: 600,
                width: "200px",
              }}
              onClick={handleSaveModalPj}
            >
              Salvar
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Modal CLT */}
      <Modal open={isBeneficiaryModalOpenClt} onClose={handleCloseBeneficiaryModalClt}>
        <Box sx={{ 
          position: "absolute" as "absolute", 
          top: "50%", 
          left: "50%", 
          transform: "translate(-50%, -50%)", 
          width: "600px", 
          maxHeight: "80vh", 
          bgcolor: "background.paper", 
          boxShadow: 24, 
          borderRadius: "12px", 
          display: "flex", 
          flexDirection: "column", 
          p: 3 
        }}>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6" sx={{ fontSize: '20px', fontWeight: 600, color: '#000' }}>
              Campos CLT
            </Typography>
            <IconButton onClick={handleCloseBeneficiaryModalClt} sx={{ color: '#666' }}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Subtitle */}
          <Typography variant="body2" sx={{ color: 'rgba(0, 0, 0, 0.6)', mb: 2 }}>
            Configure os campos que serão necessários para campanhas de dados faltantes CLT
          </Typography>

          {/* Search Field */}
          <TextField
            fullWidth
            placeholder="Digite para pesquisar"
            value={searchModalTermClt}
            onChange={(e) => setSearchModalTermClt(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#999' }} />
                </InputAdornment>
              ),
            }}
            sx={{ 
              mb: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
              }
            }}
          />

          {/* Table Container */}
          <Box sx={{ flexGrow: 1, maxHeight: '400px', overflow: 'auto' }}>
            <TableContainer sx={{ "& .MuiTableCell-root": { background: "#FFFFFF !important" } }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, color: '#000', fontSize: '14px', padding: '12px 16px', backgroundColor: '#fff !important' }}>
                      Campo
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#000', fontSize: '14px', padding: '12px 16px', textAlign: 'center', backgroundColor: '#fff !important' }}>
                      Visível
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#000', fontSize: '14px', padding: '12px 16px', textAlign: 'center', backgroundColor: '#fff !important' }}>
                      Obrigatoriedade
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredModalFieldsClt.map((field) => (
                    <TableRow key={field.key} sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}>
                      <TableCell sx={{ padding: '12px 16px', fontSize: '14px', color: '#000' }}>
                        {field.label}
                      </TableCell>
                      <TableCell sx={{ padding: '12px 16px', textAlign: 'center' }}>
                        <Switch
                          checked={field.visible}
                          disabled={mandatoryFieldsClt.includes(field.key)}
                          onChange={() => handleFieldToggleModalClt(field.key, 'visible')}
                          color="primary"
                          size="small"
                        />
                      </TableCell>
                      <TableCell sx={{ padding: '12px 16px', textAlign: 'center' }}>
                        <Switch
                          checked={field.mandatory}
                          disabled={!field.visible || mandatoryFieldsClt.includes(field.key)}
                          onChange={() => handleFieldToggleModalClt(field.key, 'mandatory')}
                          color="primary"
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
            <Button
              variant="outlined"
              color="error"
              style={{
                paddingTop: "0.25rem",
                paddingBottom: "0.25rem",
                paddingLeft: "1rem",
                paddingRight: "1rem",
                borderRadius: "0.375rem",
                height: "2.5rem",
                fontSize: "0.875rem",
                lineHeight: "1.25rem",
                fontWeight: 600,
                width: "200px",
              }}
              onClick={handleCancelModalClt}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              style={{
                paddingTop: "0.25rem",
                paddingBottom: "0.25rem",
                paddingLeft: "1rem",
                paddingRight: "1rem",
                borderRadius: "0.375rem",
                height: "2.5rem",
                fontSize: "0.875rem",
                lineHeight: "1.25rem",
                fontWeight: 600,
                width: "200px",
              }}
              onClick={handleSaveModalClt}
            >
              Salvar
            </Button>
          </Box>
        </Box>
      </Modal>

      <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: "30px", marginTop: "30px", marginBottom: "50px" }}>
        <FormControl fullWidth >
          <StyledInputLabel>Tabela(s) de entrada</StyledInputLabel>
          <StyledSelect
            multiple
            disabled
            value={activity.tables.filter(table => table.type === "INPUT").map(table => table.name)}
            // onChange={(e) => setTest(e.target.value as any)}
            renderValue={(selected) => {
              //@ts-ignore
              const visible = selected.slice(0, 2) as string[];
              //@ts-ignore
              const hiddenCount = selected.length - 2;

              return (
                <div style={{ display: "flex", flexDirection: "row", gap: "4px" }}>
                  {visible.map((value) => (
                    <Chip key={value} label={value} />
                  ))}
                  {hiddenCount > 0 && <Chip label={`+${hiddenCount}`} />}
                </div>
              );
            }}
            style={{ height: "55.3px", marginBottom: "2.7px" }}
          >
          </StyledSelect>
        </FormControl>
        {/* <FormControl fullWidth>
          <InputLabel id="multiple-select-label-a">Tabela(s) de entrada</InputLabel>
          <Select
            labelId="multiple-select-label-a"
            multiple
            value={activity.tables.filter(table => table.type === "INPUT").map(table => table.name)}
            disabled
            // onChange={handleChange}
            // input={<OutlinedInput label="Tabela(s) de destino" />}
            renderValue={(selected) => {
              // return (
              //   <div style={{ display: "flex", flexDirection: "row", gap: "100px" }}>
              //     <span>{selected}as</span>
              //   </div>
              // )
              return selected.join(', ')
            }}
          >
          </Select>
        </FormControl> */}
        <FormControl fullWidth >
          <StyledInputLabel>Tabela(s) de saída</StyledInputLabel>
          <StyledSelect
            multiple
            disabled
            value={activity.tables.filter(table => table.type === "OUTPUT").map(table => table.name)}
            style={{ height: "55.3px", marginBottom: "2.7px" }}
            renderValue={(selected) => {
              //@ts-ignore
              const visible = selected.slice(0, 2) as string[];
              //@ts-ignore
              const hiddenCount = selected.length - 2;

              return (
                <div style={{ display: "flex", flexDirection: "row", gap: "4px" }}>
                  {visible.map((value) => (
                    <Chip key={value} label={value} />
                  ))}
                  {hiddenCount > 0 && <Chip label={`+${hiddenCount}`} />}
                </div>
              );
            }}
          ></StyledSelect>
        </FormControl>
        <FormControl fullWidth >
          <StyledInputLabel>Connectores de entrada</StyledInputLabel>
          <StyledSelect
            multiple
            value={activity.connectors.filter(connector => connector.type === "INPUT").map(connector => connector.name)}
            disabled
            style={{ height: "55.3px", marginBottom: "2.7px" }}
            renderValue={(selected) => {
              //@ts-ignore
              const visible = selected.slice(0, 2) as string[];
              //@ts-ignore
              const hiddenCount = selected.length - 2;

              return (
                <div style={{ display: "flex", flexDirection: "row", gap: "4px" }}>
                  {visible.map((value) => (
                    <Chip key={value} label={value} />
                  ))}
                  {hiddenCount > 0 && <Chip label={`+${hiddenCount}`} />}
                </div>
              );
            }}
          ></StyledSelect>
        </FormControl>
        <FormControl fullWidth >
          <StyledInputLabel>Connectores de saída</StyledInputLabel>
          <StyledSelect
            multiple
            value={activity.connectors.filter(connector => connector.type === "OUTPUT").map(connector => connector.name)}
            disabled
            style={{ height: "55.3px", marginBottom: "2.7px" }}
            renderValue={(selected) => {
              //@ts-ignore
              const visible = selected.slice(0, 2) as string[];
              //@ts-ignore
              const hiddenCount = selected.length - 2;

              return (
                <div style={{ display: "flex", flexDirection: "row", gap: "4px" }}>
                  {visible.map((value) => (
                    <Chip key={value} label={value} />
                  ))}
                  {hiddenCount > 0 && <Chip label={`+${hiddenCount}`} />}
                </div>
              );
            }}
          ></StyledSelect>
        </FormControl>

        {/* Editable Fields Selector */}
        {activity.name === 'Editar dados' && (
          <FormControl fullWidth>
            <StyledEditableFieldsContainer onClick={handleOpenDropdown}>
              <StyledEditableFieldsLabel>
                Campos editáveis 
              </StyledEditableFieldsLabel>
              <StyledChipsContainer>
                {selectedFields.slice(0, 4).map((field) => (
                  <StyledEditableChip
                    key={field.id}
                    label={field.name}
                    onDelete={() => handleRemoveField(field.id)}
                    deleteIcon={<CloseIcon />}
                  />
                ))}
                {selectedFields.length > 4 && (
                  <StyledEditableChip
                    label={`+${selectedFields.length - 4}`}
                  />
                )}
              </StyledChipsContainer>
              <ExpandMoreIcon />
            </StyledEditableFieldsContainer>

            <Popover
              open={isDropdownOpen}
              anchorEl={anchorEl}
              onClose={handleCloseDropdown}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              PaperProps={{
                style: {
                  width: anchorEl?.clientWidth || 530,
                  maxHeight: 250,
                  borderRadius: '8px',
                  boxShadow: '0px 16px 32px -4px rgba(12,12,13,0.1), 0px 4px 4px -4px rgba(12,12,13,0.05)',
                },
              }}
            >
              <StyledDropdownContainer>
                <StyledSearchField
                  fullWidth
                  placeholder="Pesquisar campo"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  size="small"
                />
                {filteredFields.map((field) => (
                  <FormControlLabel
                    key={field.id}
                    control={
                      <Checkbox
                        checked={(activity.params.editableFields || []).includes(field.id)}
                        onChange={() => handleFieldToggle(field)}
                        color="primary"
                      />
                    }
                    label={field.name}
                    style={{ 
                      display: 'flex', 
                      width: '100%', 
                      margin: 0,
                    }}
                  />
                ))}
              </StyledDropdownContainer>
            </Popover>
            <Box sx={{ paddingLeft: '14px', paddingTop: '3px' }}>
              <Box
                component="div"
                sx={{
                  fontSize: '12px',
                  color: 'rgba(15, 15, 30, 0.6)',
                  lineHeight: 1.66,
                }}
              >
                Campos que podem ser editados pelos usuários
              </Box>
            </Box>
          </FormControl>
        )}

        {activity.name === "Campanhas" && campaignPanel && (
          campaignPanel.state ? (
            <CampaignTypesPanel state={campaignPanel.state} draft={campaignPanel.draft} onChange={campaignPanel.onChange} />
          ) : (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography variant="body2">{campaignPanel.error ?? "Carregando as campanhas do contrato..."}</Typography>
              {campaignPanel.error && <Button variant="outlined" onClick={campaignPanel.onRetry}>Tentar novamente</Button>}
            </Box>
          )
        )}

        {(activity.name === "Campanhas" || activity.name === "Inserção manual") && !campaignPanel && (
          <>
            <StyledBeneficiaryFieldsContainer>
              <StyledBeneficiaryLabel>
                Campos Pessoa Jurídica (PJ)
              </StyledBeneficiaryLabel>
              <StyledBeneficiaryChipsContainer>
                {visibleFieldsPj.filter(field => field.visible).map((field) => (
                  <StyledBeneficiaryChip
                    key={field.name}
                    label={field.name}
                  />
                ))}
              </StyledBeneficiaryChipsContainer>
              <Button
                variant="text"
                onClick={handleOpenBeneficiaryModalPj}
                sx={{
                  color: '#3024e1',
                  textTransform: 'none',
                  fontSize: '14px',
                  fontWeight: 500,
                }}
              >
                Editar
              </Button>
            </StyledBeneficiaryFieldsContainer>
            {/* <Box sx={{ paddingLeft: '14px', paddingTop: '3px' }}>
              <Box
                component="div"
                sx={{
                  fontSize: '12px',
                  color: 'rgba(15, 15, 30, 0.6)',
                  lineHeight: 1.66,
                }}
              >
                Campos da Pessoa Jurídica que serão exibidos
              </Box>
            </Box> */}

            {/* Configuração CLT */}
            <StyledBeneficiaryFieldsContainer>
              <StyledBeneficiaryLabel>
                Campos CLTs
              </StyledBeneficiaryLabel>
              <StyledBeneficiaryChipsContainer>
                {visibleFieldsClt.filter(field => field.visible).map((field) => (
                  <StyledBeneficiaryChip
                    key={field.name}
                    label={field.name}
                  />
                ))}
              </StyledBeneficiaryChipsContainer>
              <Button
                variant="text"
                onClick={handleOpenBeneficiaryModalClt}
                sx={{
                  color: '#3024e1',
                  textTransform: 'none',
                  fontSize: '14px',
                  fontWeight: 500,
                }}
              >
                Editar
              </Button>
            </StyledBeneficiaryFieldsContainer>
            {/* <Box sx={{ paddingLeft: '14px', paddingTop: '3px' }}>
              <Box
                component="div"
                sx={{
                  fontSize: '12px',
                  color: 'rgba(15, 15, 30, 0.6)',
                  lineHeight: 1.66,
                }}
              >
                Campos CLT que serão exibidos
              </Box>
            </Box> */}
          </>
        )}

        {/* {!!activity.tables.length && <input value={activity.tables.map(a => a.name).join(", ")} disabled />}
        <br></br>
        {!!activity.connectors.length && <input value={activity.connectors.map(c => c.name).join(", ")} disabled />}
        <br></br> */}
        {Object.entries(activity.params).map(([key, valye]) => (
          <>
              {key === "scheduling" && (
                <StyledTextField
                  fullWidth
                  value={activity.params[key]}
                  label="Frequência"
                  onChange={(e) =>
                    handleChangeActivity(index, { ...activity, params: { ...activity.params, [key]: e.target.value } })
                  }
                  variant="outlined"
                />
              )}
              {/* {key === "scheduling" && <input value={activity.params[key]} onChange={(e) => handleChangeActivity(index, { ...activity, params: { ...activity.params, [key]: e.target.value } })} />} */}
              {activity.type !== "APP" && key === "isAutomatic" && (
                <FormControl fullWidth>
                  <StyledInputLabel>Automático</StyledInputLabel>
                  <StyledSelect
                    value={activity.params[key]}
                    onChange={(e) =>
                        //@ts-ignore
                        handleChangeActivity(index, { ...activity, params: { ...activity.params, [key]: e.target.value } })
                      }
                    style={{ height: "55.3px", marginBottom: "2.7px" }}
                  >
                    <MenuItem value={"sim"}>Sim</MenuItem>
                    <MenuItem value={"nao"}>Nao</MenuItem>
                  </StyledSelect>
                </FormControl>
              )}
              {/* {key === "isAutomatic" && <input value={activity.params[key]} onChange={(e) => handleChangeActivity(index, { ...activity, params: { ...activity.params, [key]: e.target.value } })} />} */}
              {key === "layoutId" && (
                <FormControl fullWidth>
                  <InputLabel>Layout</InputLabel>
                  <Select
                      // value={layout}
                      value={activity.params[key]}
                      onChange={(e) =>
                        handleChangeActivity(index, { ...activity, params: { ...activity.params, [key]: e.target.value } })
                      }
                      // onChange={(e) => setLayout(e.target.value)}
                  >
                    {allowedLayouts.map(lay => (
                      <MenuItem key={lay.id} value={lay.title}>{lay.title}</MenuItem>
                    ))}
                  </Select>
              </FormControl>
              )}
              {/* {key === "layoutId" && <input value={activity.params[key]} onChange={(e) => handleChangeActivity(index, { ...activity, params: { ...activity.params, [key]: e.target.value } })} />} */}
          </>
        ))}
      </div>
    </>
  );
}
